/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { Typography, TextField } from '@mui/material';
import { NotificationManager } from 'react-notifications';
import React, { useEffect, useState } from 'react';
import OutlineButton from '../MUICustomButton/OutlineButton';
import GradientButton from '../MUICustomButton/GradientButton';
import { getAllAppConfig, updateAppConfig } from 'src/services/appConfigService';
//@ts-ignore
import { HelpCircleIcon } from '@hugeicons-pro/core-stroke-rounded';
import { HugeiconsIcon } from '@hugeicons/react';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import {checkEventPermission} from "../../common/utility/utility";
const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.white,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

interface AppConfig {
    configName: string;
    configType: string;
    configValue: string;
    description?: string;
}

interface ApiResponse {
    status: number;
    data: {
        result: number;
        data: AppConfig[];
    };
}

function AppConfigDetailsPage(): JSX.Element {
    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    const [appConfigurationData, setAppConfigurationData] = useState<AppConfig[]>([]);
    const [groupedData, setGroupedData] = useState<Record<string, AppConfig[]>>({});
    const [editingConfigType, setEditingConfigType] = useState<string | null>(null);
    const [editableData, setEditableData] = useState<Record<string, AppConfig[]>>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});

    useEffect(() => {
        void fetchAppConfigDetails();
    }, []);

    useEffect(() => {
        if (appConfigurationData.length > 0) {
            const grouped = groupByConfigType(appConfigurationData);
            setGroupedData(grouped);
            setEditableData(JSON.parse(JSON.stringify(grouped))); // Deep copy for editing
        }
    }, [appConfigurationData]);

    const groupByConfigType = (data: AppConfig[]): Record<string, AppConfig[]> => {
        return data.reduce((acc: Record<string, AppConfig[]>, item: AppConfig) => {
            if (!Object.prototype.hasOwnProperty.call(acc, item.configType)) {
                acc[item.configType] = [];
            }
            acc[item.configType].push(item);
            return acc;
        }, {});
    };

    const fetchAppConfigDetails = async (): Promise<void> => {
        setIsDataLoading(true);
        try {
            const res: ApiResponse = await getAllAppConfig();
            if (res.status === 200 && res.data.result === 1) {
                setAppConfigurationData(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching config: ", error);
        } finally {
            setIsDataLoading(false);
        }
    };

    const handleEditClick = (configType: string): void => {
        setEditingConfigType(configType);
        // Reset validation errors when starting to edit
        setValidationErrors({});
    };

    const handleInputChange = (configType: string, index: number, newValue: string): void => {
        setEditableData((prevData) => {
            const updatedData = { ...prevData };
            updatedData[configType][index].configValue = newValue;
            return updatedData;
        });
        
        // Clear error for this field when user starts typing
        if (validationErrors[configType]?.[index] !== "" && validationErrors[configType]?.[index] !== undefined) {
            setValidationErrors(prev => {
                const updated = { ...prev };
                if (updated[configType] !== undefined && updated[configType] !== null) { 
                    updated[configType] = [...updated[configType]];
                    updated[configType][index] = '';
                }
                return updated;
            });
        }
    };

    const handleFormReset = (): void => {
        setEditingConfigType(null);
        setValidationErrors({});
    };

    const validateForm = (): boolean => {
        if (editingConfigType === null || editingConfigType === undefined) return false;
        
        const newErrors: Record<string, string[]> = {};
        let isValid = true;
        
        // Check for empty values in the current editing section
        editableData[editingConfigType].forEach((config, index) => {
            if (config.configValue.trim() === "") {
                if (newErrors[editingConfigType] === undefined) {
                    newErrors[editingConfigType] = [];
                }
                newErrors[editingConfigType][index] = 'This field is required';
                isValid = false;
            }
        });
        
        setValidationErrors(newErrors);
        return isValid;
    };

    const handleSaveClick = async (): Promise<void> => {
        if (editingConfigType === null || editingConfigType === undefined) return;
        
        // Validate the form before submission
        if (!validateForm()) {
            return;
        }
    
        setIsLoading(true);
        try {
            // Extract data for the currently edited config type
            const updatedConfigs = editableData[editingConfigType];
    
            // Call the update service with the selected config type and its updated values
            const response = await updateAppConfig(editingConfigType, updatedConfigs);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (response.status === 200 && response.data.result === 1) {
                NotificationManager.success("App config updated successfully");
    
                // Refresh data after update
                await fetchAppConfigDetails();
                setEditingConfigType(null);
                setValidationErrors({});
            } else {
                NotificationManager.error("Failed to update configuration");
            }
        } catch (error) {
            console.error('Error saving config:', error);
        } finally {
            setIsLoading(false);
        }
    };
    
    return (
        <React.Fragment>
            <div className="flex justify-between items-center mb-5">
                <div>
                    <h2 className="text-2xl font-semibold text-title ">App Configuration</h2>
                </div>
            </div>

            <div className="campaign-form-container-appconfig" style={{ width: '100%' }}>
                {isDataLoading ? (
                    <div className="flex justify-center items-center relative top-[50%]">
                        <span className="loading loading-spinner text-primary"></span>
                    </div>
                ) : 
                (
                    Object.keys(groupedData).map((configType) => (
                        <div key={configType} className="form-sub-container-appconfig mb-5">
                            <div className="flex justify-between items-center mb-4">
                                <Typography variant="h6" sx={{ color: '#3A3A3A', fontSize: '16px', fontWeight: '600' }}>
                                    {configType}
                                </Typography>
                                {checkEventPermission("APPCONFIG_UPDATE") && 
                                <OutlineButton color="gray" onClick={() => handleEditClick(configType)} width="80px">
                                    <img src="/assets/images/icons/EditIcon.svg" width={15} height={15} alt="" style={{ marginRight: '10px' }} /> Edit
                                </OutlineButton>
                }
                            </div>

                            <div className="grid grid-cols-2 gap-x-10 gap-y-4">
                                {groupedData[configType].map((config, index) => (
                                    <div key={index} className="w-full">
                                        {editingConfigType !== configType ? (
                                            <>
                                                <div className="flex items-center gap-1 mb-2">
                                                    <Typography variant="h6" sx={{color: '#3A3A3A',fontSize: '14px',fontWeight: '600',marginBottom: '10px'}}>
                                                        {config.configName}
                                                    </Typography>
                                                    <BootstrapTooltip title={config.description}>
                                                    <span>
                                                        <HugeiconsIcon
                                                        icon={HelpCircleIcon}
                                                        size={18}
                                                        color="#3A3A3A"
                                                        strokeWidth={0.2}
                                                        style={{ marginBottom: '10px', cursor: 'pointer' }}
                                                        />
                                                    </span>
                                                    </BootstrapTooltip>
                                                </div>
                                                <Typography sx={{color: '#3A3A3A',fontSize: '14px',marginBottom: '20px'}}>
                                                    {config.configValue.length > 50 ? `${config.configValue.substring(0, 50)}...`: config.configValue}
                                                </Typography>
                                            </>
                                        ) : 
                                        (
                                        <TextField
                                            fullWidth
                                            autoComplete="off"
                                            label={config.configName}
                                            placeholder={editableData[configType][index].configValue || config.configName}
                                            value={editableData[configType][index].configValue}
                                            onChange={(e) => handleInputChange(configType, index, e.target.value)}
                                            error={Boolean(validationErrors[configType]?.[index])}
                                            helperText={
                                                typeof validationErrors[configType]?.[index] === 'string' &&
                                                validationErrors[configType]?.[index].trim() !== '' ? (
                                                    <span className="flex items-center text-red-500">
                                                        <img src="/assets/images/attention.svg" alt="error" width={12} height={12} className="mr-1"/>
                                                        {validationErrors[configType]?.[index]}
                                                    </span>
                                                ) : undefined
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                <BootstrapTooltip title={config.description}>
                                                    <span>
                                                    <HugeiconsIcon
                                                        icon={HelpCircleIcon}
                                                        size={18}
                                                        color="#3A3A3A"
                                                        strokeWidth={0.2}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                    </span>
                                                </BootstrapTooltip>
                                                )
                                            }}
                                            required
                                        />
                                    )}
                                    </div>
                                ))}
                            </div>

                            {editingConfigType === configType && (
                                <div className='flex gap-2 justify-end mt-4'>
                                    <OutlineButton color='gray' onClick={handleFormReset} width='130px'>Cancel</OutlineButton>
                                    {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                                    <GradientButton color='red' onClick={handleSaveClick} width='130px'>
                                        {!isLoading ? 'Save' : <span className="loading loading-spinner loading-xs text-white"></span>}
                                    </GradientButton>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </React.Fragment>
    );
}

export default AppConfigDetailsPage;